import { useEffect, useRef, useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7816vr2hsar0k5m03lg';
const BARKY_URL = 'https://storage.readdy-site.link/project_files/c43d99a8-1e00-428b-96a3-32143e86ff11/d6c2f9b6-4541-4f92-a27c-4e3d3602e9a5_barky_sem_fundo.png?v=6c769196e5f19b9901d880a680aabc0f';

const industrySegments = [
  'New Construction & Remodeling',
  'Roofing, Solar & Home Battery',
  'Pool & Outdoor Living',
  'Kitchen & Bathroom Design',
  'Driveway, Pavers & Turf',
  'Epoxy Flooring & Painting',
  'Commercial Maintenance',
  'Other',
];

const budgetRanges = [
  'Under $100K',
  '$100K – $500K',
  '$500K – $1M',
  '$1M – $2.5M',
  '$2.5M – $5M',
  '$5M+',
];

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [selectedSegment, setSelectedSegment] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setSelectedSegment('');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  const inputClass = 'w-full bg-white border border-teal-100 focus:border-[#DC5014] outline-none text-[#143C3C] font-sans text-base px-4 rounded-sm transition-colors duration-300 placeholder-[rgba(20,60,60,0.3)]';
  const selectClass = 'w-full bg-white border border-teal-100 focus:border-[#DC5014] outline-none text-[#143C3C] font-sans text-base px-4 rounded-sm transition-colors duration-300 cursor-pointer appearance-none';
  const labelClass = 'font-sans text-[rgba(20,60,60,0.6)] text-xs tracking-widest uppercase mb-2 block';

  return (
    <section id="contact" className="py-20 md:py-28 px-5 sm:px-8 md:px-6" style={{ background: '#FFFFFF' }} ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">

          {/* Left Info */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <p className="font-sans text-[#DC5014] text-xs tracking-[0.4em] uppercase font-500 mb-5">
              Get In Touch
            </p>

            {/* Barky + Title row */}
            <div className="flex items-end gap-4 mb-4">
              {/* Barky mascot */}
              <div className="relative flex-shrink-0 flex flex-col items-center">
                <div
                  className="rounded-xl px-3 py-2 text-center mb-2"
                  style={{
                    background: '#143C3C',
                    border: '1px solid rgba(20,60,60,0.2)',
                    color: '#FFFFFF',
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    lineHeight: 1.4,
                    position: 'relative',
                    whiteSpace: 'nowrap',
                  }}
                >
                  I&apos;ll find the right leads for you!
                  <span
                    style={{
                      position: 'absolute',
                      bottom: '-7px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '7px solid transparent',
                      borderRight: '7px solid transparent',
                      borderTop: '7px solid #143C3C',
                    }}
                  />
                </div>
                <img
                  src={BARKY_URL}
                  alt="Barky mascot"
                  style={{ width: '100px', height: 'auto', objectFit: 'contain', display: 'block' }}
                />
              </div>

              {/* Title */}
              <h2 style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: '#143C3C', fontSize: 'clamp(28px, 4vw, 48px)', lineHeight: 1.1 }}>
                START YOUR<br />
                <span style={{ color: '#DC5014' }}>PROJECT.</span>
              </h2>
            </div>

            <div className="w-16 h-px mb-8" style={{ background: '#DC5014' }} />
            <p className="font-sans text-base leading-relaxed font-300 mb-10 md:mb-12" style={{ color: 'rgba(20,60,60,0.65)', fontWeight: 300 }}>
              Our team responds within 24 hours. The first consultation is always complimentary.
              Let&apos;s build something extraordinary together.
            </p>

            {/* Contact Details */}
            <div className="flex flex-col gap-5 mb-10 md:mb-12">
              {[
                { icon: 'ri-map-pin-2-line', label: 'Showroom', value: '601 N Magnolia Ave, Orlando, FL 32801' },
                { icon: 'ri-phone-line', label: 'Phone', value: '(943) 238-4912' },
                { icon: 'ri-mail-line', label: 'Email', value: 'support@barkfy.com' },
                { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Sat: 9am – 6pm EST' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-sm" style={{ background: 'rgba(20,60,60,0.06)', border: '1px solid rgba(20,60,60,0.1)' }}>
                    <i className={`${item.icon} text-base`} style={{ color: '#DC5014' }} />
                  </div>
                  <div>
                    <p className="font-sans text-xs tracking-widest uppercase mb-1" style={{ color: 'rgba(20,60,60,0.45)' }}>{item.label}</p>
                    <p className="font-sans text-sm font-500" style={{ color: '#143C3C' }}>{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Image */}
            <div className="relative rounded-sm overflow-hidden" style={{ height: '200px' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/3dfbed65197387c67d5d8554be0aacf9.jpg"
                alt="Barkfy Downtown Orlando Office"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(20,60,60,0.4) 0%, transparent 60%)' }} />
            </div>
          </div>

          {/* Right: Form */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="rounded-sm p-6 md:p-8" style={{ background: '#FAFAFA', border: '1px solid rgba(20,60,60,0.08)' }}>
              <h3 className="font-bold text-2xl uppercase tracking-wider mb-8" style={{ fontFamily: '"Playfair Display", serif', color: '#143C3C' }}>
                Free Consultation Request
              </h3>

              {submitStatus === 'success' ? (
                <div className="py-16 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: 'rgba(20,60,60,0.08)', border: '1px solid rgba(20,60,60,0.2)' }}>
                    <i className="ri-check-line text-2xl" style={{ color: '#143C3C' }} />
                  </div>
                  <h4 className="font-bold text-xl uppercase tracking-wider mb-2" style={{ fontFamily: '"Playfair Display", serif', color: '#143C3C' }}>Message Sent!</h4>
                  <p className="font-sans text-sm" style={{ color: 'rgba(20,60,60,0.5)' }}>Our team will contact you within 24 hours.</p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="mt-6 font-sans text-xs tracking-widest uppercase underline cursor-pointer"
                    style={{ color: '#DC5014' }}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>First Name</label>
                      <input
                        name="first_name"
                        required
                        placeholder="Carlos"
                        className={inputClass}
                        style={{ height: '52px' }}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Last Name</label>
                      <input
                        name="last_name"
                        required
                        placeholder="Martinez"
                        className={inputClass}
                        style={{ height: '52px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="carlos@email.com"
                      className={inputClass}
                      style={{ height: '52px' }}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="(407) 000-0000"
                      className={inputClass}
                      style={{ height: '52px' }}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Industry Segment</label>
                    <select
                      name="industry_segment"
                      required
                      className={selectClass}
                      style={{ height: '52px' }}
                      value={selectedSegment}
                      onChange={(e) => setSelectedSegment(e.target.value)}
                    >
                      <option value="">Select your industry...</option>
                      {industrySegments.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  {selectedSegment === 'Other' && (
                    <div>
                      <label className={labelClass}>Please Specify Your Industry</label>
                      <input
                        name="industry_other"
                        required
                        placeholder="Describe your industry..."
                        className={inputClass}
                        style={{ height: '52px' }}
                      />
                    </div>
                  )}
                  <div>
                    <label className={labelClass}>Project Budget</label>
                    <select
                      name="budget"
                      className={selectClass}
                      style={{ height: '52px' }}
                    >
                      <option value="">Select budget range...</option>
                      {budgetRanges.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Project Description</label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your dream project..."
                      className={`${inputClass} resize-none py-3`}
                    />
                    <p className="font-sans text-xs mt-1 text-right" style={{ color: 'rgba(20,60,60,0.35)' }}>Max 500 characters</p>
                  </div>

                  {submitStatus === 'error' && (
                    <p className="font-sans text-xs tracking-wider" style={{ color: '#DC5014' }}>
                      Something went wrong. Please try again.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitStatus === 'sending'}
                    className="w-full font-bold text-sm tracking-widest uppercase rounded-sm disabled:opacity-50 transition-all duration-300 cursor-pointer whitespace-nowrap mt-2"
                    style={{ background: '#DC5014', color: '#FFFFFF', height: '56px', minHeight: '56px', border: 'none' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#BF4411'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#DC5014'; }}
                  >
                    {submitStatus === 'sending' ? 'SENDING...' : 'APPLY FOR THE HUB'}
                  </button>

                  <p className="font-sans text-xs text-center tracking-wider" style={{ color: 'rgba(20,60,60,0.35)' }}>
                    No commitment required. Confidential.
                  </p>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
